import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import axios from 'axios'
import {
  appendHelcimPayIframe,
  removeHelcimPayIframe,
  setupPaymentEventListener,
  PAYMENT_CONSTANTS,
} from '../payment-utils'

interface ReservationData {
  id: string
  customer_name: string
  customer_email: string
  customer_phone: string
  space_type: string
  space_dimensions: string
  monthly_rate: number
  prorated_amount: number
  move_in_date: string
  access_options: { need_24hr: boolean; need_3x_week: boolean }
  payment_token?: string
  status: string
}

type PageState = 'loading' | 'summary' | 'paying' | 'completing' | 'success' | 'error' | 'already-paid'

const PhonePaymentPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const reservationId = searchParams.get('reservationId')
  const hasSigned = searchParams.get('hasSigned') === 'true'

  const [pageState, setPageState] = useState<PageState>('loading')
  const [reservation, setReservation] = useState<ReservationData | null>(null)
  const [error, setError] = useState<string>('')

  // Load reservation data
  useEffect(() => {
    if (!reservationId) {
      setError('No reservation ID provided')
      setPageState('error')
      return
    }

    loadReservation()
  }, [reservationId])

  const loadReservation = async () => {
    try {
      setPageState('loading')
      // Fetch reservation details via a dedicated endpoint
      const response = await axios.post('/.netlify/functions/phone-get-reservation', {
        reservation_id: reservationId,
      })

      const data = response.data
      if (!data.success) {
        throw new Error(data.error || 'Failed to load reservation')
      }

      setReservation(data.reservation)

      if (data.reservation.status === 'completed' || data.reservation.status === 'payment-confirmed') {
        setPageState('already-paid')
      } else {
        setPageState('summary')
      }
    } catch (err: any) {
      console.error('Error loading reservation:', err)
      setError(err.response?.data?.error || err.message || 'Failed to load reservation')
      setPageState('error')
    }
  }

  const handlePay = async () => {
    if (!reservation || !reservationId) return

    try {
      setPageState('paying')

      // Prepare payment if we don't have a token yet
      let checkoutToken = reservation.payment_token
      if (!checkoutToken) {
        const paymentResponse = await axios.post('/.netlify/functions/phone-prepare-payment', {
          reservation_id: reservationId,
        })

        if (!paymentResponse.data.success) {
          throw new Error(paymentResponse.data.error || 'Failed to prepare payment')
        }

        checkoutToken = paymentResponse.data.checkout_token
      }

      // Setup payment listener
      const cleanup = setupPaymentEventListener(
        checkoutToken!,
        async (eventMessage: any) => {
          // Payment success
          setPageState('completing')
          removeHelcimPayIframe()

          try {
            // Complete the payment — activate lease, update records
            await axios.post('/.netlify/functions/phone-complete-payment', {
              reservation_id: reservationId,
              transaction_id: eventMessage.transactionId,
              invoice_number: eventMessage.invoiceNumber,
              customer_code: eventMessage.customerCode,
              amount: eventMessage.amount,
              payment_method: eventMessage.cardNumber ? 'cc' : 'ach',
              paid_date: eventMessage.dateCreated,
            })

            setPageState('success')
          } catch (err: any) {
            console.error('Error completing payment:', err)
            setError('Payment received but there was an error finalizing your reservation. Please call us at (614) 699-2495.')
            setPageState('error')
          }

          cleanup()
        },
        () => {
          // Payment aborted
          removeHelcimPayIframe()
          setPageState('summary')
          cleanup()
        },
        () => {
          // Payment hidden/cancelled
          removeHelcimPayIframe()
          setPageState('summary')
          cleanup()
        }
      )

      // Show the Helcim iframe
      appendHelcimPayIframe(
        checkoutToken!,
        true,
        reservation.customer_phone,
        reservation.customer_email
      )
    } catch (err: any) {
      console.error('Error preparing payment:', err)
      setError(err.response?.data?.error || err.message || 'Failed to start payment')
      setPageState('error')
    }
  }

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)

  const containerStyle: React.CSSProperties = {
    maxWidth: '600px',
    margin: '0 auto',
    padding: '2rem 1.5rem',
    minHeight: '60vh',
  }

  const cardStyle: React.CSSProperties = {
    background: '#fff',
    borderRadius: '12px',
    padding: '2rem',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    marginBottom: '1.5rem',
  }

  if (pageState === 'loading') {
    return (
      <div style={containerStyle}>
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <h2 style={{ color: '#232043' }}>Loading your reservation...</h2>
          <p>Please wait a moment.</p>
        </div>
      </div>
    )
  }

  if (pageState === 'error') {
    return (
      <div style={containerStyle}>
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <h2 style={{ color: '#e74c3c' }}>Something went wrong</h2>
          <p style={{ color: '#666', marginTop: '1rem' }}>{error}</p>
          <p style={{ color: '#666', marginTop: '1rem' }}>
            Need help? Call us at <a href="tel:6146992495" style={{ color: '#232043', fontWeight: 600 }}>(614) 699-2495</a>
          </p>
          <button
            onClick={loadReservation}
            style={{ marginTop: '1.5rem', background: '#232043', color: 'yellow', padding: '0.75rem 2rem', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (pageState === 'already-paid') {
    return (
      <div style={containerStyle}>
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <h2 style={{ color: '#27ae60' }}>{PAYMENT_CONSTANTS.EMOJI_CHECK_MARK} Payment Complete!</h2>
          <p style={{ color: '#666', marginTop: '1rem' }}>
            Your reservation is confirmed. You'll receive details via text and email.
          </p>
          <p style={{ color: '#666', marginTop: '0.5rem' }}>
            Don't forget to complete your profile — we'll send you a link to upload your driver's license and insurance.
          </p>
        </div>
      </div>
    )
  }

  if (pageState === 'success') {
    return (
      <div style={containerStyle}>
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <h2 style={{ color: '#27ae60' }}>{PAYMENT_CONSTANTS.EMOJI_CHECK_MARK} You're all set!</h2>
          <p style={{ color: '#232043', fontWeight: 600, fontSize: '1.1rem', marginTop: '1rem' }}>
            Your space is reserved, {reservation?.customer_name?.split(' ')[0]}!
          </p>
          <p style={{ color: '#666', marginTop: '0.5rem' }}>
            You'll receive your lease agreement and move-in details via text and email shortly.
          </p>
          <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
            <p style={{ color: '#232043', fontWeight: 600, marginBottom: '0.5rem' }}>Next steps:</p>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>
              We'll text you a link to upload your driver's license and proof of insurance.
              Your gate access code will be sent once everything is verified.
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (pageState === 'completing') {
    return (
      <div style={containerStyle}>
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <h2 style={{ color: '#232043' }}>{PAYMENT_CONSTANTS.EMOJI_CREDIT_CARD} Finalizing your reservation...</h2>
          <p style={{ color: '#666' }}>Please wait while we activate your space.</p>
        </div>
      </div>
    )
  }

  // Summary state — show reservation details and pay button
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h2 style={{ color: '#232043', marginBottom: '1.5rem', textAlign: 'center' }}>
          Complete Your Reservation
        </h2>

        {reservation && (
          <>
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ color: '#232043', marginBottom: '0.5rem', fontSize: '1rem' }}>Space Details</h3>
              <div style={{ color: '#666' }}>
                <div>{reservation.space_type} — {reservation.space_dimensions}</div>
                <div>Move-in: {new Date(reservation.move_in_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
                {reservation.access_options?.need_24hr && <div>+ 24-hour gate access</div>}
                {reservation.access_options?.need_3x_week && <div>+ 3x weekly gate access</div>}
              </div>
            </div>

            <div style={{ borderTop: '1px solid #eee', paddingTop: '1rem', marginBottom: '1.5rem' }}>
              <h3 style={{ color: '#232043', marginBottom: '0.5rem', fontSize: '1rem' }}>Payment Summary</h3>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#666', marginBottom: '0.25rem' }}>
                <span>Prorated rent (rest of month)</span>
                <span>{formatCurrency(reservation.prorated_amount)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#666', marginBottom: '0.25rem' }}>
                <span>Security deposit (1 month)</span>
                <span>{formatCurrency(reservation.monthly_rate)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#232043', fontWeight: 700, borderTop: '1px solid #eee', paddingTop: '0.5rem', marginTop: '0.5rem', fontSize: '1.1rem' }}>
                <span>Total due today</span>
                <span>{formatCurrency(reservation.prorated_amount + reservation.monthly_rate)}</span>
              </div>
            </div>

            <button
              onClick={handlePay}
              style={{
                width: '100%',
                background: '#232043',
                color: 'yellow',
                padding: '1rem',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '1.1rem',
                letterSpacing: '1px',
                cursor: 'pointer',
              }}
            >
              {PAYMENT_CONSTANTS.EMOJI_CREDIT_CARD} Pay {formatCurrency(reservation.prorated_amount + reservation.monthly_rate)}
            </button>

            <p style={{ color: '#999', fontSize: '0.8rem', textAlign: 'center', marginTop: '1rem' }}>
              Secure payment powered by Helcim. Your card details are never stored on our servers.
            </p>
          </>
        )}
      </div>
    </div>
  )
}

export default PhonePaymentPage
